'use client';

import { queryOptions, useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';

import { ProfessionalPrivateProfile, getProfessionalPrivateProfile } from '@website/services/professionals.service';

import { useCurrentUser } from './useCurrentUser';

/**
 * Query key factory so callers (e.g. the verification page) can invalidate
 * the current user's professional profile after a mutation without having to
 * duplicate the locale/id tuple.
 */
export function currentProfessionalQueryKey(userId: string | undefined, locale: string) {
  return ['professional', 'private', userId ?? 'anonymous', locale] as const;
}

/**
 * The query of the current user's own private professional profile (`null` when signed out), shared by
 * every hook below so they read and refresh one cache entry.
 */
function ownProfessionalProfileQuery(userId: string | undefined, locale: string) {
  return queryOptions<ProfessionalPrivateProfile | null>({
    queryKey: currentProfessionalQueryKey(userId, locale),
    queryFn: () => (userId ? getProfessionalPrivateProfile(userId, locale) : null),
    staleTime: 30 * 1000
  });
}

/**
 * Fetches the private professional profile for the currently-authenticated
 * user. Returns `null` when the user is not a professional (or not logged in).
 *
 * The private profile exposes the moderation `status`
 * (`pending` | `approved` | `rejected`) and the revisions history, both of
 * which drive the verification banner and the ID verification page.
 */
export function useCurrentProfessional() {
  const locale = useLocale();
  const { data: user } = useCurrentUser();

  const query = useQuery({
    ...ownProfessionalProfileQuery(user?.id, locale),
    enabled: !!user?.id && !!user.isProfessional
  });

  return {
    ...query,
    professional: query.data ?? null,
    isProfessional: !!user?.isProfessional
  };
}

/**
 * The current user's own professional profile in either mode (`null` when they have none), with its
 * review `status`. Asks the API only when `enabled`: a user without a profile gets a 404.
 */
export function useOwnProfessionalProfile({ enabled = true }: { enabled?: boolean } = {}) {
  const locale = useLocale();
  const { data: user } = useCurrentUser();
  return useQuery({ ...ownProfessionalProfileQuery(user?.id, locale), enabled: enabled && !!user?.id });
}
